// src/components/provider/ProviderDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaListAlt, FaBriefcase, FaUserClock, FaUserEdit,
  FaChild, FaCalendarAlt, FaClipboardList, FaUsers, FaFileAlt, FaStar
} from 'react-icons/fa';
import { getProviderJobs, getProviderWaitingList, getProviderSpots, getProviderApplications } from '../../services/firestore';
import { collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const ProviderDashboard = () => {
  const { userProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    activeJobs: 0,
    totalApplications: 0,
    activeSpots: 0,
    totalReviews: 0
  });
  const [recentWaitlist, setRecentWaitlist] = useState([]);
  const [recentJobs, setRecentJobs] = useState([]);
  const [recentApplications, setRecentApplications] = useState([]);
  const [loading, setLoading] = useState({
    stats: true,
    jobs: true,
    waitlist: true,
    applications: true
  });
  const [error, setError] = useState(null);
  
  // Fetch data from Firestore
  useEffect(() => {
    if (!currentUser) return;

    // Fetch job listings from Firestore
    const fetchJobs = async () => {
      try {
        setLoading(prev => ({ ...prev, jobs: true }));
        const jobsList = await getProviderJobs(currentUser.uid);
        
        // Only take the most recent jobs for the dashboard
        const sortedJobs = jobsList.sort((a, b) => {
          const dateA = a.startDate ? new Date(a.startDate) : new Date(0);
          const dateB = b.startDate ? new Date(b.startDate) : new Date(0);
          return dateB - dateA; // Most recent first
        });
        
        const recentJobsList = sortedJobs.slice(0, 3); // Show only the 3 most recent jobs
        setRecentJobs(recentJobsList);
        
        // Update open jobs count in stats
        setStats(prevStats => ({
          ...prevStats,
          activeJobs: jobsList.filter(job => job.status === 'open').length
        }));
      } catch (error) {
        console.error('Error fetching jobs:', error);
        setError('Failed to load job listings. Please try again later.');
      } finally {
        setLoading(prev => ({ ...prev, jobs: false }));
      }
    };
    
    // Fetch waitlist entries
    const fetchWaitlist = async () => {
      try {
        setLoading(prev => ({ ...prev, waitlist: true }));
        const waitlistEntries = await getProviderWaitingList(currentUser.uid);
        
        // Only take the most recent entries with "pending" status for the dashboard
        const pendingEntries = waitlistEntries
          .filter(entry => entry.status === 'pending' || entry.status === 'contacted')
          .sort((a, b) => (a.position || 0) - (b.position || 0))
          .slice(0, 3); // Show only the first 3 entries
        
        setRecentWaitlist(pendingEntries);
        
        // Update waitlist length in stats
        setStats(prevStats => ({
          ...prevStats,
          activeSpots: 0 // Keep this for now
        }));
      } catch (error) {
        console.error('Error fetching waitlist:', error);
        setError('Failed to load waitlist entries. Please try again later.');
      } finally {
        setLoading(prev => ({ ...prev, waitlist: false }));
      }
    };
    
    // Fetch applications
    const fetchApplications = async () => {
      try {
        setLoading(prev => ({ ...prev, applications: true }));
        const applications = await getProviderApplications(currentUser.uid);
        // Count unique (jobId, jobSeekerUid) pairs
        const uniquePairs = new Set();
        applications.forEach(app => {
          if (app.jobId && app.jobSeekerUid) {
            uniquePairs.add(app.jobId + '::' + app.jobSeekerUid);
          }
        });
        setStats(prevStats => ({
          ...prevStats,
          totalApplications: uniquePairs.size
        }));
        setRecentApplications(applications.slice(0, 3));
      } catch (error) {
        console.error('Error fetching applications:', error);
        setError('Failed to load applications. Please try again later.');
      } finally {
        setLoading(prev => ({ ...prev, applications: false }));
      }
    };
    
    // Fetch available spots count
    const fetchSpots = async () => {
      if (!currentUser) return;
      try {
        const spots = await getProviderSpots(currentUser.uid);
        // Only count spots with status 'available'
        const activeSpots = spots.filter(spot => spot.status === 'available').length;
        setStats(prev => ({ ...prev, activeSpots }));
      } catch (err) {
        setError('Error fetching spots: ' + err.message);
      }
    };

    fetchJobs();
    fetchWaitlist();
    fetchApplications();
    fetchSpots();
  }, [currentUser]);
  
  // Format date for display
  const formatDate = date => {
    if (!date) return 'N/A';
    
    try {
      const dateObj = date instanceof Date 
        ? date 
        : new Date(date.seconds ? date.seconds * 1000 : date);
        
      return new Intl.DateTimeFormat('en-US', {
        month: 'short', day: 'numeric', year: 'numeric'
      }).format(dateObj);
    } catch (e) {
      console.error('Date formatting error:', e);
      return 'Invalid date';
    }
  };
  
  // Get status class
  const getStatusClass = status => {
    if (!status) return '';
    
    const statusMap = {
      pending: 'status-pending', contacted: 'status-contacted', offered: 'status-offered',
      accepted: 'status-accepted', declined: 'status-declined', removed: 'status-removed'
    };
    return statusMap[status] || '';
  };

  // Navigation handlers
  const handleAddNewSpot = () => {
    navigate('/provider/spots');
  };
  
  const handlePostNewJob = () => {
    navigate('/provider/jobs');
  };
  
  const handleManageWaitlist = () => {
    navigate('/provider/waiting-list');
  };
  
  const handleViewJobDetails = (jobId) => {
    // Pass the job ID as state when navigating to the jobs page
    navigate('/provider/jobs', { state: { selectedJobId: jobId, action: 'view' } });
  };
  
  const handleEditJob = (jobId) => {
    // Pass the job ID as state when navigating to the jobs page
    navigate('/provider/jobs', { state: { selectedJobId: jobId, action: 'edit' } });
};

  // Component for stat card
  const StatCard = ({ icon: Icon, number, label, className }) => (
    <div className={`stat-card ${className}`}>
      <div className="stat-icon"><Icon /></div>
      <div className="stat-number">{number}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
  
  // Links with icons component
  const ActionLink = ({ onClick, icon: Icon, text, btnClass = "btn-primary" }) => (
    <button onClick={onClick} className={`btn ${btnClass} btn-block`}>
      <Icon className="btn-icon" /> {text}
    </button>
  );

  // Dashboard card component
  const DashboardCard = ({ to, icon: Icon, title, description }) => {
    const handleClick = (e) => {
      e.preventDefault();
      navigate(to);
    };
    
    return (
      <div onClick={handleClick} className="dashboard-card" style={{ cursor: 'pointer' }}>
        <div className="card-content">
          <div className="card-icon"><Icon /></div>
          <h3>{title}</h3>
          <p>{description}</p>
        </div>
      </div>
    );
  };
  
  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Provider Dashboard</h1>
        <p className="dashboard-welcome">Welcome back, {userProfile?.displayName || 'provider'}!</p>
        <p className="dashboard-description">Manage your childcare business and listings</p>
      </div>
      
      {error && <div className="error-message" style={{ color: '#b71c1c', background: '#fdecea', border: '1px solid #f44336', padding: '12px', borderRadius: '6px', marginBottom: '1rem', wordBreak: 'break-all' }}>
        {typeof error === 'string' ? error : (error.message || JSON.stringify(error))}
      </div>}
      
      {/* Stats Overview */}
      <div className="stats-grid">
        <StatCard icon={FaChild} number={stats.activeSpots} label="Active Spots" className="primary" />
        <StatCard icon={FaBriefcase} number={stats.activeJobs} label="Active Jobs" className="secondary" />
        <StatCard icon={FaFileAlt} number={stats.totalApplications} label="Job Applications" className="info" />
      </div>
      
      <div className="dashboard-layout">
        <div className="dashboard-main">
          {/* Waitlist Entries */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Recent Waitlist Entries</h2>
              <div className="section-actions">
                <Link to="/provider/waiting-list" className="btn btn-outline btn-sm">View All</Link>
              </div>
            </div>
            <div className="card">
              <div className="card-body p-0 scrollable-table-container">
                {loading.waitlist ? (
                  <div className="loading-container">
                    <p>Loading waitlist entries...</p>
                  </div>
                ) : recentWaitlist.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Position</th><th>Child</th><th>Age Category</th>
                        <th>Status</th><th>Date Requested</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentWaitlist.map(entry => (
                        <tr key={entry.id} className="waitlist-row">
                          <td className="position-column">{entry.position || 'N/A'}</td>
                          <td>
                            <div>{entry.childData?.name || 'N/A'}</div>
                            <div className="text-muted">{entry.parentName || 'N/A'}</div>
                          </td>
                          <td>{entry.childData?.ageCategory || 'N/A'}</td>
                          <td>
                            <span className={`status-badge ${getStatusClass(entry.status)}`}>
                              {entry.status ? entry.status.charAt(0).toUpperCase() + entry.status.slice(1) : 'Unknown'}
                            </span>
                          </td>
                          <td>{formatDate(entry.createdAt)}</td>
                          <td>
                            <div className="table-actions">
                              <Link to="/provider/waiting-list" className="btn btn-outline btn-sm">Details</Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state"><p>No waitlist entries found.</p></div>
                )}
              </div>
            </div>
          </section>
          
          {/* Recent Applications */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Recent Job Applications</h2>
              <div className="section-actions">
                <Link to="/provider/applications" className="btn btn-outline btn-sm">View All</Link>
              </div>
            </div>
            <div className="card">
              <div className="card-body p-0 scrollable-table-container">
                {loading.applications ? (
                  <div className="loading-container">
                    <p>Loading applications...</p>
                  </div>
                ) : recentApplications.length > 0 ? (
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Applicant</th><th>Job Title</th><th>Applied</th><th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentApplications.map(application => (
                        <tr key={application.id} className="application-row">
                          <td>{application.jobSeekerName || 'N/A'}</td>
                          <td>{application.jobTitle || 'N/A'}</td>
                          <td>{formatDate(application.createdAt)}</td>
                          <td>
                            <div className="table-actions">
                              <button 
                                onClick={() => navigate(`/jobseeker/${application.jobSeekerUid}`)}
                                className="btn btn-outline btn-sm"
                              >
                                View Profile
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="empty-state">
                    <p>No applications received yet.</p>
                  </div>
                )}
              </div>
            </div>
          </section>
          
          {/* Open Jobs */}
          <section className="dashboard-section">
            <div className="section-header">
              <h2 className="section-title">Open Job Positions</h2>
              <div className="section-actions">
                <Link to="/provider/jobs" className="btn btn-outline btn-sm">View All</Link>
                <button 
                  onClick={handlePostNewJob} 
                  className="btn btn-primary btn-sm"
                >
                  Post New Job
                </button>
              </div>
            </div>
            
            <div className="jobs-list">
              {loading.jobs ? (
                <div className="loading-container">
                  <p>Loading job listings...</p>
                </div>
              ) : recentJobs.length > 0 ? recentJobs.map(job => (
                <div className="job-card" key={job.id}>
                  <div className="job-header">
                    <h3>{job.title || 'Untitled Job'}</h3>
                    <span className={`job-type ${job.duration || ''}`}>
                      {job.duration ? job.duration.charAt(0).toUpperCase() + job.duration.slice(1) : 'Unknown'}
                    </span>
                  </div>
                  
                  <div className="job-details">
                    <div className="job-info">
                      <p><strong>Posted:</strong> {formatDate(job.startDate || new Date())}</p>
                      <p><strong>Applications:</strong> {job.applications || 0}</p>
                    </div>
                    
                    <div className="job-actions">
                      <button 
                        onClick={() => handleViewJobDetails(job.id)}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </button>
                      <button 
                        onClick={() => handleEditJob(job.id)}
                        className="btn btn-primary btn-sm"
                      >
                        Edit
                      </button>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="empty-state">
                  <p>No jobs posted yet.</p>
                  <button onClick={handlePostNewJob} className="btn btn-primary">Post a Job</button>
                </div>
              )}
            </div>
          </section>
        </div>
        
        <div className="dashboard-sidebar">
          {/* Recent Activity */}
          <div className="card mt-4">
            <div className="card-header">
              <h2 className="card-title">Recent Activities</h2>
            </div>
            <div className="card-body">
              <div className="activity-feed">
                {loading.applications ? (
                  <div className="loading-container">
                    <p>Loading activities...</p>
                  </div>
                ) : recentApplications.length > 0 ? (
                  recentApplications.map(application => (
                    <div 
                      className="feed-item" 
                      key={application.id}
                      onClick={() => navigate(`/jobseeker/${application.jobSeekerUid}`)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="feed-icon">🎉</div>
                      <div className="feed-content">
                        <div className="feed-title">
                          {application.jobSeekerName} applied for {application.jobTitle}
                        </div>
                        <div className="feed-time">
                          {formatDate(application.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="feed-item">
                    <div className="feed-icon">🎉</div>
                    <div className="feed-content">
                      <div className="feed-title">Welcome to your dashboard!</div>
                      <div className="feed-time">Just now</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Actions */}
      <section className="dashboard-section">
        <div className="section-header">
          <h2 className="section-title">Manage Your Business</h2>
        </div>
        <div className="dashboard-cards">
          <div onClick={() => navigate('/provider/spots')} className="dashboard-card" style={{ cursor: 'pointer' }}>
            <div className="card-content">
              <div className="card-icon"><FaListAlt /></div>
              <h3>Manage Spots</h3>
              <p>Add and update available childcare spots</p>
            </div>
          </div>
          
          <div onClick={() => navigate('/provider/jobs')} className="dashboard-card" style={{ cursor: 'pointer' }}>
            <div className="card-content">
              <div className="card-icon"><FaBriefcase /></div>
              <h3>Post Jobs</h3>
              <p>Create job listings for staff positions</p>
            </div>
          </div>
          
          <div onClick={() => navigate('/provider/waiting-list')} className="dashboard-card" style={{ cursor: 'pointer' }}>
            <div className="card-content">
              <div className="card-icon"><FaUserClock /></div>
              <h3>Waiting List</h3>
              <p>Manage your waitlist and enrollment</p>
            </div>
          </div>
          
          <div onClick={() => navigate('/provider/profile')} className="dashboard-card" style={{ cursor: 'pointer' }}>
            <div className="card-content">
              <div className="card-icon"><FaUserEdit /></div>
              <h3>Edit Profile</h3>
              <p>Update your profile and business details</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProviderDashboard;